/**
 * Generic registry view. Systems, workflows, integrations and the data dictionary all render
 * through this one component, driven by the schema the backend advertises.
 */

import { useMemo, useState } from "react";

import { registry as registryApi } from "../api/resources";
import { Card, EmptyState, ErrorBanner, Field, Loading, Modal, StatusBadge } from "./ui";
import { useAsync } from "../lib/useAsync";
import { displayValue } from "../lib/format";
import type { RegistryRecord } from "../lib/types";

export interface RegistryTableProps {
  organizationId: string;
  resource: string;
  columns: Array<{ key: string; label: string; kind?: "status" | "list" | "text" }>;
  createFields: Array<{ key: string; label: string; required?: boolean; options?: string[] }>;
  emptyTitle: string;
  emptyDescription: string;
}

export function RegistryTable({
  organizationId,
  resource,
  columns,
  createFields,
  emptyTitle,
  emptyDescription,
}: RegistryTableProps) {
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | undefined>();

  const records = useAsync(
    () => registryApi.list(organizationId, resource, { limit: 200 }),
    [organizationId, resource],
  );

  const rows = useMemo(() => records.data?.items ?? [], [records.data]);

  async function create() {
    setError(undefined);
    const payload: Record<string, unknown> = {};
    for (const field of createFields) {
      const value = draft[field.key];
      if (value !== undefined && value !== "") payload[field.key] = value;
    }
    try {
      await registryApi.create(organizationId, resource, payload);
      setCreating(false);
      setDraft({});
      records.reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  async function remove(record: RegistryRecord) {
    setError(undefined);
    try {
      await registryApi.remove(organizationId, resource, record.id);
      records.reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  return (
    <>
      <ErrorBanner error={records.error ?? error} />
      <Card
        title={`${rows.length} record${rows.length === 1 ? "" : "s"}`}
        actions={
          <button className="primary" onClick={() => setCreating(true)}>
            Add
          </button>
        }
        flush
      >
        {records.loading ? (
          <Loading />
        ) : rows.length === 0 ? (
          <EmptyState
            title={emptyTitle}
            description={emptyDescription}
            action={
              <button className="primary" onClick={() => setCreating(true)}>
                Add manually
              </button>
            }
          />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th key={column.key}>{column.label}</th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((record) => (
                  <tr key={record.id}>
                    {columns.map((column) => {
                      const value = record[column.key];
                      return (
                        <td key={column.key} className={column.kind === "text" ? "small muted" : ""}>
                          {column.kind === "status" ? (
                            <StatusBadge status={value ? String(value) : "unknown"} />
                          ) : Array.isArray(value) ? (
                            value.length ? (
                              <div className="row" style={{ gap: 4 }}>
                                {value.slice(0, 4).map((item, index) => (
                                  <span key={index} className="badge badge--neutral">
                                    {String(item)}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              "—"
                            )
                          ) : (
                            <span className="truncate">{displayValue(value)}</span>
                          )}
                        </td>
                      );
                    })}
                    <td>
                      <button className="btn--small btn--danger" onClick={() => void remove(record)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {creating ? (
        <Modal
          title={`Add to ${resource.replace(/-/g, " ")}`}
          onClose={() => setCreating(false)}
          footer={
            <>
              <button onClick={() => setCreating(false)}>Cancel</button>
              <button className="primary" onClick={() => void create()}>
                Create
              </button>
            </>
          }
        >
          <p className="small muted">
            Records added here are marked <strong>proposed</strong> until someone verifies them,
            exactly like extracted knowledge.
          </p>
          {createFields.map((field) => (
            <Field key={field.key} label={field.label + (field.required ? " *" : "")}>
              {field.options ? (
                <select
                  value={draft[field.key] ?? ""}
                  onChange={(event) =>
                    setDraft((current) => ({ ...current, [field.key]: event.target.value }))
                  }
                >
                  <option value="">Not selected</option>
                  {field.options.map((option) => (
                    <option key={option} value={option}>
                      {option.replace(/_/g, " ")}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  value={draft[field.key] ?? ""}
                  onChange={(event) =>
                    setDraft((current) => ({ ...current, [field.key]: event.target.value }))
                  }
                />
              )}
            </Field>
          ))}
        </Modal>
      ) : null}
    </>
  );
}
